import type { CheckItem, CheckState, DatabaseFile, Section } from '../types';
import { emptyCheckItemMedia } from '../types';
import { parseSectionItemSortComment, serializeSectionItemSortComment } from '../utils/sectionItemSort';

const CHECK_OPEN  = /^(\s*)-\s\[ \]\s/;
const CHECK_DONE  = /^(\s*)-\s\[x\]\s/i;
const CHECK_WIP   = /^(\s*)-\s\[~\]\s/;
const PLAIN_ITEM  = /^(\s*)-\s(?!\[)/;   // plain "- " line (no checkbox)
const DATE_RE     = /^(\d{4}-\d{2}-\d{2}):\s*/;
const HEADING_RE  = /^(#{1,6})\s+(.+)$/;
const IMG_LINE_RE = /^\s*-\s+img:\s*(.+?)\s*$/i;
const AUDIO_LINE_RE = /^\s*-\s+audio:\s*(.+?)\s*$/i;
const VIDEO_LINE_RE = /^\s*-\s+video:\s*(.+?)\s*$/i;
/** Indented continuation of the previous checklist item (multiline body). */
const ITEM_CONTINUATION_RE = /^\s{2,}(\S.*)$/;
/** Items wrongly saved when `  - img:` lines were parsed as plain checklist rows. */
const IMG_ITEM_TEXT_RE = /^img:\s*(.+)$/i;
const AUDIO_ITEM_TEXT_RE = /^audio:\s*(.+)$/i;
const VIDEO_ITEM_TEXT_RE = /^video:\s*(.+)$/i;

function attachImageFilename(items: CheckItem[], filename: string): void {
  const name = filename.trim();
  if (name && !name.includes('/') && items.length > 0) {
    items[items.length - 1]!.images.push(name);
  }
}

function attachAudioFilename(items: CheckItem[], filename: string): void {
  const name = filename.trim();
  if (name && !name.includes('/') && items.length > 0) {
    items[items.length - 1]!.audio.push(name);
  }
}

function attachVideoFilename(items: CheckItem[], filename: string): void {
  const name = filename.trim();
  if (name && !name.includes('/') && items.length > 0) {
    items[items.length - 1]!.video.push(name);
  }
}

/** Merge `img:` / `audio:` / `video:` pseudo-items (from older parser) into the previous item. */
function normalizeAttachmentItems(sections: Section[]): void {
  for (const section of sections) {
    const merged: CheckItem[] = [];
    for (const item of section.items) {
      const img = IMG_ITEM_TEXT_RE.exec(item.text.trim());
      if (img) {
        attachImageFilename(merged, img[1]!);
        continue;
      }
      const audio = AUDIO_ITEM_TEXT_RE.exec(item.text.trim());
      if (audio) {
        attachAudioFilename(merged, audio[1]!);
        continue;
      }
      const video = VIDEO_ITEM_TEXT_RE.exec(item.text.trim());
      if (video) {
        attachVideoFilename(merged, video[1]!);
        continue;
      }
      merged.push(item);
    }
    section.items = merged;
  }
}

function parseState(line: string): CheckState | null {
  if (CHECK_DONE.test(line))  return 'done';
  if (CHECK_WIP.test(line))   return 'in-progress';
  if (CHECK_OPEN.test(line))  return 'open';
  if (PLAIN_ITEM.test(line))  return 'open';  // treat plain dashes as open items
  return null;
}

function stripPrefix(line: string, _state: CheckState): string {
  const stripped = line
    .replace(/^\s*-\s\[.\]\s/, '')  // checkbox prefix
    .replace(/^\s*-\s/, '')          // plain dash prefix
    .trim();
  const dateMatch = DATE_RE.exec(stripped);
  return dateMatch ? stripped.slice(dateMatch[0].length) : stripped;
}

function extractDate(line: string): string {
  const stripped = line
    .replace(/^\s*-\s\[.\]\s/, '')
    .replace(/^\s*-\s/, '')
    .trim();
  const m = DATE_RE.exec(stripped);
  return m ? m[1] : '';
}

/** Paprika-style numbered step: `1. text` or `1 text` */
function textFromNumberedLine(line: string): string | null {
  const m = /^\s*\d+(?:[.)]\s*|\s+)(\S.*)$/.exec(line.trim());
  return m ? m[1]!.trim() : null;
}

function pushPlainItem(section: Section, text: string, raw = ''): void {
  const t = text.trim();
  if (!t) return;
  section.items.push({
    raw,
    text: t,
    date: '',
    state: 'open',
    ...emptyCheckItemMedia(),
  });
}

function isChecklistLine(line: string): boolean {
  return parseState(line) !== null;
}

function sectionTitleFromLine(line: string): string {
  const trimmed = line.trim();
  return trimmed.length > 120 ? 'Items' : trimmed;
}

function newSection(title: string): Section {
  return { title, level: 2, items: [], prose: [] };
}

/** Plain-text title line after items (or a second title) → new section for imports without ## headings. */
function tryStartSectionFromPlainTitleLine(
  line: string,
  current: Section,
  flush: () => void
): Section | null {
  const trimmed = line.trim();
  if (!trimmed || isChecklistLine(line) || textFromNumberedLine(line)) return null;
  if (current.items.length === 0 && trimmed === current.title) return null;
  flush();
  return newSection(sectionTitleFromLine(line));
}

/** Recipe exports and other prose-only lines → editable checklist rows. */
export function migrateProseToItems(sections: Section[]): void {
  for (const section of sections) {
    for (const line of section.prose) {
      const numbered = textFromNumberedLine(line);
      pushPlainItem(section, numbered ?? line);
    }
    section.prose = [];
  }
}

/** Re-run prose migration on an open file (e.g. cached before parser fix). */
export function refreshFileFromProse(file: DatabaseFile): void {
  migrateProseToItems(file.sections);
}

function isIndentedItemContinuationLine(line: string): boolean {
  if (!/^\s{2,}/.test(line)) return false;
  if (HEADING_RE.test(line.trim())) return false;
  if (parseState(line) !== null) return false;
  return true;
}

function nextNonBlankLine(lines: string[], fromIndex: number): string | null {
  for (let j = fromIndex + 1; j < lines.length; j++) {
    if (lines[j]!.trim() !== '') return lines[j]!;
  }
  return null;
}

export class MarkdownService {
  refreshFileFromProse(file: DatabaseFile): void {
    migrateProseToItems(file.sections);
  }

  parse(path: string, content: string, sha: string): DatabaseFile {
    const lines = content.split('\n');
    const sections: Section[] = [];
    let current: Section | null = null;

    const flush = () => { if (current) sections.push(current); };

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex]!;
      const headingMatch = HEADING_RE.exec(line);
      if (headingMatch) {
        const level = headingMatch[1].length;
        if (level === 1) continue; // `# Title` is the file name, not a section
        flush();
        current = {
          title: headingMatch[2].trim(),
          level,
          items: [],
          prose: [],
        };
        continue;
      }

      if (!current) {
        const trimmed = line.trim();
        if (trimmed === '' || line.startsWith('---')) continue;

        const firstState = parseState(line);
        const title = firstState !== null ? 'Items' : sectionTitleFromLine(line);
        current = newSection(title);
        if (firstState !== null) {
          current.items.push({
            raw: line,
            text: stripPrefix(line, firstState),
            date: extractDate(line),
            state: firstState,
            ...emptyCheckItemMedia(),
          });
        }
        continue;
      }

      const sortFromComment = parseSectionItemSortComment(line);
      if (sortFromComment) {
        current.itemSort = sortFromComment;
        continue;
      }

      const imgMatch = IMG_LINE_RE.exec(line);
      if (imgMatch) {
        attachImageFilename(current.items, imgMatch[1]!);
        continue;
      }

      const audioMatch = AUDIO_LINE_RE.exec(line);
      if (audioMatch) {
        attachAudioFilename(current.items, audioMatch[1]!);
        continue;
      }

      const videoMatch = VIDEO_LINE_RE.exec(line);
      if (videoMatch) {
        attachVideoFilename(current.items, videoMatch[1]!);
        continue;
      }

      if (line.trim() === '' && !line.startsWith('---') && current.items.length > 0) {
        const next = nextNonBlankLine(lines, lineIndex);
        if (next && isIndentedItemContinuationLine(next)) {
          const prev = current.items[current.items.length - 1]!;
          prev.text = `${prev.text}\n`;
        }
        continue;
      }

      // Indented lines are multiline body on the previous item (receipt line items, notes), not new rows.
      if (/^\s{2,}/.test(line) && current.items.length > 0) {
        const trimmedCont = line.trimStart();
        if (trimmedCont === '') {
          const prev = current.items[current.items.length - 1]!;
          prev.text = `${prev.text}\n`;
          continue;
        }
        const dashBody = /^-\s+(?!img:)(?!audio:)(?!video:)(.+)$/i.exec(trimmedCont);
        const part = dashBody ? dashBody[1]!.trim() : trimmedCont;
        if (part) {
          const prev = current.items[current.items.length - 1]!;
          prev.text = prev.text ? `${prev.text}\n${part}` : part;
          continue;
        }
      }

      const state = parseState(line);
      if (state !== null) {
        current.items.push({
          raw: line,
          text: stripPrefix(line, state),
          date: extractDate(line),
          state,
          ...emptyCheckItemMedia(),
        });
        continue;
      }

      const contMatch = ITEM_CONTINUATION_RE.exec(line);
      if (contMatch && current.items.length > 0) {
        const tail = contMatch[1]!;
        if (
          !tail.startsWith('- ') &&
          !IMG_LINE_RE.test(line) &&
          !AUDIO_LINE_RE.test(line) &&
          !VIDEO_LINE_RE.test(line)
        ) {
          const prev = current.items[current.items.length - 1]!;
          prev.text = prev.text ? `${prev.text}\n${tail.trim()}` : tail.trim();
          continue;
        }
      }

      if (line.trim() !== '' && !line.startsWith('---')) {
        const numbered = textFromNumberedLine(line);
        if (numbered !== null) {
          pushPlainItem(current, numbered);
          continue;
        }
        const nextSection = tryStartSectionFromPlainTitleLine(line, current, flush);
        if (nextSection) {
          current = nextSection;
          continue;
        }
        pushPlainItem(current, line);
        continue;
      }
    }
    flush();
    migrateProseToItems(sections);
    normalizeAttachmentItems(sections);

    const name = path
      .split('/').pop()!
      .replace('.md', '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());

    return {
      path,
      name,
      sections,
      sha,
      orphanedImagePaths: [],
      orphanedAudioPaths: [],
      orphanedVideoPaths: [],
    };
  }

  serialize(file: DatabaseFile, originalContent: string): string {
    // Full rebuild from the in-memory model.
    // We track original lines by their raw string for state changes,
    // but also support new items/sections that have no original raw line.
    const lines: string[] = [];
    const firstLine = originalContent.split('\n')[0] ?? '';
    const fileTitle = firstLine.startsWith('# ') ? firstLine : `# ${file.name}`;
    lines.push(fileTitle, '');

    for (const section of file.sections) {
      lines.push(`## ${section.title}`, '');
      const sortComment = serializeSectionItemSortComment(section.itemSort);
      if (sortComment) lines.push(sortComment, '');
      for (const prose of section.prose) {
        lines.push(prose);
      }
      if (section.prose.length > 0) lines.push('');
      for (const item of section.items) {
        const datePart = item.date ? `${item.date}: ` : '';
        const textLines = item.text.split('\n');
        const firstText = textLines[0] ?? '';
        // Preserve checkbox format for items that came from checkbox lines (daily report reads them)
        // Use plain dash for items that had no checkbox originally
        const hadCheckbox = item.raw.match(/^-\s\[.\]/);
        if (hadCheckbox || item.raw === '') {
          const marker = item.state === 'done' ? '[x]' : item.state === 'in-progress' ? '[~]' : '[ ]';
          lines.push(`- ${marker} ${datePart}${firstText}`);
        } else {
          lines.push(`- ${datePart}${firstText}`);
        }
        for (let ti = 1; ti < textLines.length; ti++) {
          lines.push(`  ${textLines[ti]}`);
        }
        for (const img of item.images) {
          lines.push(`  - img: ${img}`);
        }
        for (const clip of item.audio) {
          lines.push(`  - audio: ${clip}`);
        }
        for (const clip of item.video) {
          lines.push(`  - video: ${clip}`);
        }
      }
      lines.push('');
    }

    // Remove trailing blank lines except one
    while (lines.length > 1 && lines[lines.length - 1] === '' && lines[lines.length - 2] === '') {
      lines.pop();
    }

    return lines.join('\n');
  }
}
